import React, { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// custom hooks
import { useRequest } from 'hooks/useRequest';
import { useProjectStepper } from 'hooks/useProjectStepperState';
// api
import api from 'utils/api';
import {
  IProject,
  useUpdateProjectMutation,
  useUpdateProjectTemplateMutation,
} from 'redux-store/api/projects';
// validations
import { SetScheduleSchema } from 'layouts/set-schedule-form/validation';
// types
import { ITemplate } from 'utils/types';

import SetScheduleNewUI from './new-ui';
import { LagTypeEnum } from 'redux-store/api/company';
import { zonedDateToUtc } from 'utils/postDateHelper';

const SetSchedule: React.FC = () => {
  const [updateProject, { isLoading: loadingProject }] = useUpdateProjectMutation();
  const { handleNext, projectId, handleMutate, projectDetails } = useProjectStepper();
  const [updateProjectTemplate, { isLoading: loadingTemplate }] =
    useUpdateProjectTemplateMutation();
  const loading = loadingTemplate || loadingProject;

  const form = useForm({
    resolver: yupResolver(SetScheduleSchema),
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const { data: templates } = useRequest<{ items: ITemplate[] }>(
    '/templates',
    api.templates.getTemplatesList,
    {
      onSuccess: (data) => {
        form.setValue('templateId', data.items[0].id);
      },
    }
  );

  const onSubmit = useCallback(
    async (formValues) => {
      if (projectId) {
        const isBlank = formValues.templateId === -1;

        const data = isBlank
          ? { canBeEmpty: true }
          : { templateId: formValues.templateId, startDate: zonedDateToUtc(formValues.startDate) };

        new Promise((resolve) => {
          updateProject({
            id: projectId,
            data: { lagType: formValues.lagType as LagTypeEnum },
          });

          updateProjectTemplate({
            projectId: projectId,
            ...data,
          })
            .unwrap()
            .then((projectData) => {
              resolve(projectData);
            });
        }).then((res: IProject) => {
          handleMutate(res);
          if (res) window.dispatchEvent(new CustomEvent('project_updated'));

          handleNext();
        });
      }
    },
    [handleNext, projectId, updateProjectTemplate]
  );

  const isWorkday = (date: Date) => {
    const day = date.getDay();
    return projectDetails?.workingDays?.every((item) => day !== item);
  };

  const props = {
    loading,
    templates,
    form,
    onSubmit,
    isWorkday,
  };

  return <SetScheduleNewUI {...props} />;
};

export default SetSchedule;
